import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { fetchBlogList, fetchBlogPost } from "../lib/api";

export default function Blog() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [posts, setPosts] = useState([]);
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    if (slug) {
      fetchBlogPost(slug)
        .then((d) => mounted && setPost(d.post))
        .catch(() => mounted && setPost(null))
        .finally(() => mounted && setLoading(false));
    } else {
      fetchBlogList()
        .then((d) => mounted && setPosts(d.posts || []))
        .catch(() => mounted && setPosts([]))
        .finally(() => mounted && setLoading(false));
    }
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (slug) {
    if (loading) {
      return (
        <div className="bg-charcoal pt-40 pb-24 min-h-screen" data-testid="blog-loading">
          <div className="max-w-3xl mx-auto px-6">
            <div className="h-[60vh] bg-charcoal-surface animate-pulse" />
          </div>
        </div>
      );
    }
    if (!post) {
      return (
        <div className="bg-charcoal pt-40 pb-24 min-h-screen text-center" data-testid="blog-notfound">
          <p className="text-cream-muted">Article not found.</p>
          <Link to="/journaal" className="btn-gold-outline mt-8 inline-flex">
            {t.blog.back}
          </Link>
        </div>
      );
    }
    const title = post[`title_${lang}`] || post.title_nl;
    const body = post[`body_${lang}`] || post.body_nl;
    return (
      <article className="bg-charcoal pt-28 pb-24" data-testid="page-blog-post">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
          <Link to="/journaal" className="inline-flex items-center gap-2 text-cream-muted hover:text-gold text-xs uppercase tracking-[0.22em] mb-10" data-testid="blog-back">
            <ArrowLeft size={14} strokeWidth={1.5} /> {t.blog.back}
          </Link>
          <div className="aspect-[16/8] overflow-hidden mb-12 bg-charcoal-surface">
            <img src={post.image} alt={title} className="w-full h-full object-cover" />
          </div>
          <p className="overline">
            {t.blog.publishedOn} {post.date} · {t.blog.by} {post.author}
          </p>
          <h1 className="font-serif text-4xl md:text-6xl text-cream leading-[1.05] mt-4 mb-12">
            {title}
          </h1>
          <div className="text-cream-muted text-base leading-relaxed font-light prose-luxury max-w-3xl">
            {body.split("\n\n").map((para, i) =>
              para.startsWith("## ") ? (
                <h2 key={i} className="font-serif text-2xl md:text-3xl text-cream mt-12 mb-4">
                  {para.replace(/^##\s+/, "")}
                </h2>
              ) : (
                <p key={i} className="mb-6 whitespace-pre-wrap">
                  {para}
                </p>
              )
            )}
          </div>
        </div>
      </article>
    );
  }

  return (
    <div className="bg-charcoal pt-32 pb-24" data-testid="page-blog">
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
        <div className="mb-14">
          <p className="overline">{t.blog.overline}</p>
          <h1 className="font-serif text-4xl md:text-6xl text-cream leading-[1.05] mt-4 max-w-3xl">
            {t.blog.title}
          </h1>
          <span className="divider-gold mt-6" />
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-10">
            {[1, 2, 3].map((i) => (
              <div key={i} className="aspect-[4/3] bg-charcoal-surface animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {posts.map((p) => {
              const title = p[`title_${lang}`] || p.title_nl;
              const excerpt = p[`excerpt_${lang}`] || p.excerpt_nl;
              return (
                <Link
                  key={p.slug}
                  to={`/journaal/${p.slug}`}
                  className="group block"
                  data-testid={`blog-card-${p.slug}`}
                >
                  <div className="prop-img-wrap aspect-[4/3] overflow-hidden mb-5 bg-charcoal-surface">
                    <img src={p.image} alt={title} className="w-full h-full object-cover" />
                  </div>
                  <p className="overline" style={{ fontSize: "0.65rem" }}>
                    {p.date}
                  </p>
                  <h3 className="font-serif text-2xl md:text-3xl text-cream group-hover:text-gold transition-colors leading-tight mt-3">
                    {title}
                  </h3>
                  <p className="text-cream-muted text-sm mt-3 leading-relaxed">{excerpt}</p>
                  <p className="text-gold mt-5 text-xs tracking-[0.22em] uppercase inline-flex items-center gap-2">
                    {t.blog.readMore} <ArrowRight size={12} strokeWidth={1.5} />
                  </p>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
